import * as React from "react";
import { CheckIcon, CopyIcon } from "lucide-react";

import { cn } from "@acme/ui";
import { Input } from "@acme/ui/input";
import { toast } from "@acme/ui/toast";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@acme/ui/tooltip";

export interface InputWithCopyProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    "onCopy" | "value"
  > {
  value: string;
  onCopy?: (value: string) => void;
  copySuccessMessage?: string;
  copyErrorMessage?: string;
}

const InputWithCopy = React.forwardRef<HTMLInputElement, InputWithCopyProps>(
  (
    {
      className,
      value,
      onCopy,
      copySuccessMessage = "Copied to clipboard",
      copyErrorMessage = "Failed to copy to clipboard",
      ...props
    },
    ref,
  ) => {
    const [copied, setCopied] = React.useState<boolean>(false);

    const handleCopy = React.useCallback(async () => {
      try {
        await navigator.clipboard.writeText(value);
        onCopy?.(value);
        toast.success(copySuccessMessage);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      } catch {
        toast.error(copyErrorMessage);
      }
    }, [value, onCopy, copySuccessMessage, copyErrorMessage]);

    return (
      <div className="relative mt-2">
        <Input
          ref={ref}
          className={cn("bg-muted/30 pe-9", className)}
          value={value}
          readOnly
          {...props}
        />
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={handleCopy}
                className="text-muted-foreground/80 hover:text-foreground focus-visible:border-ring focus-visible:ring-ring/50 absolute inset-y-0 end-0 flex h-full w-9 items-center justify-center rounded-e-md transition-[color,box-shadow] outline-none focus:z-10 focus-visible:ring-[3px] disabled:pointer-events-none disabled:cursor-not-allowed"
                aria-label={copied ? "Copied" : "Copy to clipboard"}
                disabled={copied}
              >
                <div
                  className={cn(
                    "transition-all",
                    copied ? "scale-100 opacity-100" : "scale-0 opacity-0",
                  )}
                >
                  <CheckIcon
                    className="text-primary"
                    size={16}
                    aria-hidden="true"
                  />
                </div>
                <div
                  className={cn(
                    "absolute transition-all",
                    copied ? "scale-0 opacity-0" : "scale-100 opacity-100",
                  )}
                >
                  <CopyIcon size={16} aria-hidden="true" />
                </div>
              </button>
            </TooltipTrigger>
            <TooltipContent className="px-2 py-1 text-xs">
              Copy to clipboard
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    );
  },
);

InputWithCopy.displayName = "InputWithCopy";

export { InputWithCopy };
